import type { FeatureContext } from './featureContext';
import type { EntityId } from '../sim/entity';
// Type-only: erased at build, so the window/composition chunk stays lazy (the
// implementation is reached exclusively via the dynamic `import()` below).
import type { initPaperdoll, PaperdollHandle, PaperdollView } from '../paperdoll';

/**
 * The paperdoll feature (PRD #629 Slice 3, ADR-0329): double-clicking any player
 * character - self or another player - opens the one reusable read-only paperdoll
 * gump showing that player's composed appearance.
 *
 * Fully client-side (zero new wire traffic): the target's look is the replicated
 * `renderable` the world renderer already draws from, resolved here and handed to
 * the window. The window + composition renderer are a **lazy chunk**: this module
 * (statically reachable from `main.ts`) imports only the window's *types* (erased
 * at build - not a module edge) and dynamic-`import()`s the implementation on the
 * first double-click, so a player who never opens a paperdoll pays no load cost
 * (story 28; pinned by `paperdollRenderer.lazy.test.ts`).
 *
 * Lifecycle: the open window follows the target's replicated appearance (a
 * look-key compare per snapshot rebuilds only on real change, story 13), closes
 * when the target leaves the replicated scene (story 19), and resets on a
 * reconnect's Welcome so no stale target survives. The self-only `Dress` action
 * emits the client-local `openWardrobe` hub event the wardrobe installer owns.
 */

/** Injectable seams for unit tests; the default loader is the real lazy chunk. */
export interface PaperdollInstallDeps {
  loadPaperdoll?: () => Promise<{ initPaperdoll: typeof initPaperdoll }>;
}

export function installPaperdoll(ctx: FeatureContext, deps: PaperdollInstallDeps = {}): void {
  const load = deps.loadPaperdoll ?? (() => import('../paperdoll'));

  let handle: PaperdollHandle | undefined;
  let handleLoad: Promise<PaperdollHandle | undefined> | undefined;
  /** The entity the open window is showing (undefined = closed / cleared). */
  let targetId: EntityId | undefined;
  /** The look key last rendered, so unchanged 10Hz snapshots never repaint. */
  let lastKey: string | undefined;

  const clearTarget = (): void => {
    targetId = undefined;
    lastKey = undefined;
  };

  /** Resolve a target's current replicated appearance, or undefined if gone. */
  const viewFor = (id: EntityId): PaperdollView | undefined => {
    const entity = ctx.world()?.get(id);
    if (!entity?.mobile) return undefined; // left the scene (or never a character)
    const renderable = entity.renderable ?? {};
    return {
      name: (renderable.name ?? '').trim(),
      self: id === ctx.playerId(),
      look: {
        body: renderable.body,
        bodyHue: renderable.bodyHue,
        equipment: (renderable.equipment ?? []).map((item) => ({ id: item.id, hue: item.hue })),
      },
    };
  };

  /** The rebuild-only-on-change key (the renderer's lookKey precedent + name/self). */
  const viewKey = (view: PaperdollView): string => JSON.stringify(view);

  /** Lazy-build the window on first use; a failed chunk load retries next open. */
  const ensureHandle = (): Promise<PaperdollHandle | undefined> => {
    if (!handleLoad) {
      handleLoad = load()
        .then((mod) => {
          handle = mod.initPaperdoll({
            doc: ctx.ui,
            onDress: () => ctx.events.emit('openWardrobe'),
            onClose: () => clearTarget(),
          });
          return handle;
        })
        .catch(() => {
          handleLoad = undefined; // a transient chunk-load failure may retry
          return undefined;
        });
    }
    return handleLoad;
  };

  const openFor = async (id: EntityId): Promise<void> => {
    const doll = await ensureHandle();
    if (!doll) return;
    const view = viewFor(id);
    if (!view) return; // the target vanished while the chunk loaded
    targetId = id;
    lastKey = viewKey(view);
    doll.open(view);
  };

  // The generalized character double-click seam (self + player roles; the
  // renderer gates who fires it). Re-wired per scene build, like every
  // per-renderer hook.
  ctx.onRendererBuilt((renderer) => {
    renderer.onCharacterDoubleClick = (id) => {
      void openFor(id);
    };
  });

  // Follow the open target across the snapshot stream: rebuild only when the
  // body/equipment/hues/name actually change; close when the target disappears.
  ctx.events.on('snapshot', () => {
    if (targetId === undefined || !handle?.isOpen()) return;
    const view = viewFor(targetId);
    if (!view) {
      clearTarget();
      handle.close(); // never show a stale player as present (story 19)
      return;
    }
    const key = viewKey(view);
    if (key === lastKey) return;
    lastKey = key;
    handle.setView(view);
  });

  // A (re)login rebuilds the world with fresh entity ids: drop the old target.
  ctx.events.on('welcome', () => {
    clearTarget();
    handle?.close();
  });
}
